import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, ChevronLeft, ChevronRight, MessageCircle } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import 'swiper/css';
import { supabase } from '../../supabaseClient';
import { inCatalog, prettyName, brandOf, ageShort, formatRD, navidadWhatsApp } from '../../seo/navidadText';

// Home: a short strip of Christmas kids' bikes (8 of the catalog, one wheel size after another)
// that moves by itself from right to left, with discreet arrows, and one button to the collection.
const SHOWN = 8;

// The cheapest bikes of each wheel size, alternating 12 / 16 / 20, so the strip shows the whole range
function pick(rows) {
    const byAro = {};
    rows.forEach((row) => { (byAro[row.categoria] = byAro[row.categoria] || []).push(row); });
    const aros = Object.keys(byAro).sort((a, b) => Number(a) - Number(b));
    aros.forEach((aro) => byAro[aro].sort((a, b) => Number(a.precio) - Number(b.precio)));
    const out = [];
    for (let i = 0; out.length < SHOWN && aros.some((aro) => byAro[aro][i]); i += 1) {
        aros.forEach((aro) => { if (byAro[aro][i] && out.length < SHOWN) out.push(byAro[aro][i]); });
    }
    return out;
}

const Arrow = ({ side, onClick }) => (
    <button
        type="button"
        aria-label={side === 'prev' ? 'Bicicleta anterior' : 'Siguiente bicicleta'}
        onClick={onClick}
        className={`absolute top-[34%] z-10 w-10 h-10 rounded-full bg-white/90 border border-gray-200 shadow-md flex items-center justify-center text-black hover:bg-black hover:text-white transition-all md:opacity-0 md:group-hover:opacity-100 focus-visible:opacity-100 ${side === 'prev' ? 'left-0 -translate-x-1/3 md:-translate-x-1/2' : 'right-0 translate-x-1/3 md:translate-x-1/2'}`}
    >
        {side === 'prev' ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
    </button>
);

const ChristmasCarousel = () => {
    const [bikes, setBikes] = useState(null);
    const [total, setTotal] = useState(0);
    const [cheapest, setCheapest] = useState(null);
    const swiperRef = useRef(null);

    useEffect(() => {
        supabase.from('bikes_navidad_2025').select('*').then(({ data, error }) => {
            if (error) {
                console.error('Error fetching christmas bikes:', error);
                setBikes([]);
                return;
            }
            const rows = inCatalog(data || []);
            setTotal(rows.length);
            setCheapest(rows.length ? Math.min(...rows.map((row) => Number(row.precio))) : null);
            setBikes(pick(rows));
        });
    }, []);

    // People who asked their system for less motion get a still strip they move themselves
    const reduceMotion = useMemo(() => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches, []);

    if (bikes && !bikes.length) return null;

    return (
        <section className="bg-white py-16 md:py-20" aria-labelledby="navidad-home-title">
            <div className="max-w-[1600px] mx-auto px-6 md:px-12">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8 md:mb-10">
                    <div className="max-w-3xl">
                        <p className="text-[11px] font-black uppercase tracking-widest text-red-600 mb-3">Bicicletas de Navidad para niños · Plan de apartado</p>
                        <h2 id="navidad-home-title" className="text-3xl md:text-5xl font-black italic uppercase tracking-tight leading-none">
                            El regalo de tu niño esta Navidad: <span className="gradient-text-christmas-dark">su bicicleta{cheapest ? ` desde RD$ ${formatRD(cheapest)}` : ''}</span>
                        </h2>
                        <p className="text-gray-600 mt-4 text-base md:text-lg leading-relaxed">
                            Raleigh, Julen BMX y Carolina Princess en aros 12, 16 y 20. Apártala hoy, te la entregamos armada y calibrada
                            y la retiras en Los Prados antes de Nochebuena o del Día de Reyes.
                        </p>
                        <ul className="flex flex-wrap gap-x-5 gap-y-1 mt-4 text-xs font-bold text-gray-700">
                            <li className="flex items-center gap-1.5"><Check size={14} className="text-emerald-600" /> Armada y calibrada</li>
                            <li className="flex items-center gap-1.5"><Check size={14} className="text-emerald-600" /> Apartado para Nochebuena y Reyes</li>
                            <li className="flex items-center gap-1.5"><Check size={14} className="text-emerald-600" /> Garantía y soporte en tienda</li>
                        </ul>
                    </div>
                    <Link to="/navidad" className="hidden md:inline-flex items-center gap-2 shrink-0 bg-black text-white px-7 py-4 rounded-full text-xs font-black uppercase tracking-widest hover:bg-gray-800 transition-colors">
                        Ver las {total || ''} bicicletas de Navidad <ArrowRight size={16} />
                    </Link>
                </div>

                {!bikes ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-5 animate-pulse" aria-hidden="true">
                        {Array.from({ length: 5 }, (_, i) => (
                            <div key={i} className={i > 1 ? 'hidden md:block' : ''}>
                                <div className="bg-gray-100 rounded-[1.5rem] aspect-square mb-3" />
                                <div className="h-3 w-2/3 bg-gray-100 rounded mb-2" />
                                <div className="h-4 w-1/3 bg-gray-100 rounded" />
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="relative group" aria-roledescription="carousel" aria-label="Bicicletas de Navidad destacadas">
                        <Swiper
                            modules={[Autoplay]}
                            onSwiper={(swiper) => { swiperRef.current = swiper; }}
                            loop={bikes.length > 5}
                            autoplay={reduceMotion ? false : { delay: 3000, disableOnInteraction: false, pauseOnMouseEnter: true }}
                            speed={700}
                            slidesPerView={2}
                            spaceBetween={12}
                            breakpoints={{
                                768: { slidesPerView: 3, spaceBetween: 16 },
                                1024: { slidesPerView: 4, spaceBetween: 20 },
                                1280: { slidesPerView: 5, spaceBetween: 20 }
                            }}
                        >
                            {bikes.map((bike) => (
                                <SwiperSlide key={bike.slug} className="!h-auto">
                                    <article className="h-full flex flex-col">
                                        <Link to={`/navidad/${bike.slug}/`} className="group/card block">
                                            <div className="relative bg-white border border-gray-100 rounded-[1.5rem] p-4 sm:p-6 aspect-square flex items-center justify-center shadow-sm transition-all duration-500 group-hover/card:shadow-xl group-hover/card:-translate-y-1">
                                                <span className="absolute top-3 left-3 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-black text-white">Aro {bike.categoria}"</span>
                                                <img
                                                    src={bike.imagen_url}
                                                    alt={`Bicicleta ${prettyName(bike)} aro ${bike.categoria} para niños`}
                                                    className="max-w-full max-h-full object-contain mix-blend-multiply"
                                                    loading="lazy"
                                                    decoding="async"
                                                    referrerPolicy="no-referrer"
                                                />
                                            </div>
                                            <div className="px-1 mt-3">
                                                <div className="text-[11px] font-black uppercase tracking-wider text-red-600">{brandOf(bike)}</div>
                                                <h3 className="text-sm md:text-base font-black italic uppercase tracking-tight text-black leading-tight line-clamp-2 pr-1">{prettyName(bike)}</h3>
                                                <p className="text-xs text-gray-500 mt-0.5">{ageShort(bike)}</p>
                                            </div>
                                        </Link>
                                        <div className="px-1 mt-auto pt-3 flex items-center justify-between gap-2">
                                            <span className="text-base md:text-lg font-black text-black">RD$ {formatRD(bike.precio)}</span>
                                            <a
                                                href={navidadWhatsApp(bike)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1.5 bg-black text-white text-[11px] font-black uppercase tracking-wider px-3 py-2 rounded-full hover:bg-gray-800 transition-colors"
                                            >
                                                <MessageCircle size={12} className="fill-current" /> Apartar
                                            </a>
                                        </div>
                                    </article>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                        <Arrow side="prev" onClick={() => swiperRef.current && swiperRef.current.slidePrev()} />
                        <Arrow side="next" onClick={() => swiperRef.current && swiperRef.current.slideNext()} />
                    </div>
                )}

                <div className="mt-8 md:hidden">
                    <Link to="/navidad" className="w-full inline-flex items-center justify-center gap-2 bg-black text-white px-7 py-4 rounded-full text-xs font-black uppercase tracking-widest hover:bg-gray-800 transition-colors">
                        Ver las {total || ''} bicicletas de Navidad <ArrowRight size={16} />
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default ChristmasCarousel;
